import { useCurrentUserCompanyId } from 'hooks/company';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';

import {
  useGetAllSmsConversationsQuery,
  useGetTalentSmsConversationQuery,
  useCreateSmsMutation,
  SmsSortEnum,
  SmsSortDirectionEnum,
} from '@libs/graphql-types';

export const useSmsConversations = (search?: string) => {
  const companyId = useCurrentUserCompanyId();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, error, refetch } = useGetAllSmsConversationsQuery({
    variables: {
      company_id: companyId!,
      first: 50,
      page: 1,
      sort: SmsSortEnum.CreatedAt,
      direction: SmsSortDirectionEnum.Desc,
      search: search || undefined,
    },
    skip: !companyId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 10000,
    onError: () => {
      enqueueSnackbar('Failed to load SMS conversations', { variant: 'error' });
    },
  });

  const conversations = useMemo(
    () => data?.getAllSMSConversations?.data || [],
    [data],
  );

  return {
    conversations,
    loading,
    error,
    refetch,
    paginatorInfo: data?.getAllSMSConversations?.paginatorInfo,
  };
};

export const useTalentSmsConversation = (talentId?: string) => {
  const companyId = useCurrentUserCompanyId();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading, error, refetch } = useGetTalentSmsConversationQuery({
    variables: {
      company_id: companyId!,
      talent_id: talentId!,
      first: 100,
      page: 1,
      sort: SmsSortEnum.CreatedAt,
      direction: SmsSortDirectionEnum.Asc,
    },
    skip: !companyId || !talentId,
    fetchPolicy: 'cache-and-network',
    pollInterval: 10000,
    onError: () => {
      enqueueSnackbar('Failed to load messages', { variant: 'error' });
    },
  });

  const messages = useMemo(
    () => data?.getTalentSMSConversation?.data || [],
    [data],
  );

  return {
    messages,
    loading,
    error,
    refetch,
    paginatorInfo: data?.getTalentSMSConversation?.paginatorInfo,
  };
};

export const useSendSms = (onSuccess?: () => void) => {
  const companyId = useCurrentUserCompanyId();
  const { enqueueSnackbar } = useSnackbar();

  const [sendSms, { loading }] = useCreateSmsMutation({
    onCompleted: () => {
      enqueueSnackbar('Message sent successfully', { variant: 'success' });
      onSuccess?.();
    },
    onError: (error) => {
      enqueueSnackbar(error.message || 'Failed to send message', {
        variant: 'error',
      });
    },
    refetchQueries: ['GetAllSMSConversations', 'GetTalentSMSConversation'],
  });

  const handleSendSms = async (talentId: string, content: string) => {
    if (!companyId || !talentId || !content.trim()) return;

    await sendSms({
      variables: {
        company_id: companyId,
        talent_id: talentId,
        content: content.trim(),
      },
    });
  };

  return {
    sendSms: handleSendSms,
    loading,
  };
};
